/**
 * Browse Folder Focus prototype
 *
 * Focus browse + "show all" are one toggle (Documents context menu).
 * Optional activity labels in tree. No inline control box in the tree.
 *
 * Access: http://flownotes.localhost/?test=browse-folder-focus
 * Bet: docs/product/bets/2026-05-21-browse-folder-activity-filter.md
 */

import { useMemo, useState, useCallback } from 'react'
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  FileText,
  LayoutGrid,
  Pin,
  Filter,
  Settings,
  Bookmark,
  CheckSquare,
  Boxes,
  ArrowLeft,
  Search,
  Eye,
  FilePlus,
  FolderPlus,
  Pencil,
  Move,
  MessageSquare,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/toaster'
import type { BrowseInactiveDays } from '@/lib/browse-folder-focus'
import {
  BrowseFolderFocusProvider,
  useBrowseFolderFocus,
} from '@/providers/browse-folder-focus-provider'
import {
  BrowseClientsMixProductionTree,
  BrowseSpecsProductionTree,
  buildPrototypeActivitySeed,
  PROTOTYPE_WORKSPACE_IDS,
} from '@/components/test-browse-folder-focus-specs-tree'

const MOCK_CLIENTS_PARENT = 'hop-studios/clients'

type MockClientFolder = {
  id: string
  name: string
  daysSinceActivity: number | null
}

const MOCK_CLIENTS: MockClientFolder[] = [
  { id: 'greater-good', name: 'greater-good', daysSinceActivity: 2 },
  { id: 'ggia', name: 'ggia', daysSinceActivity: 5 },
  { id: 'ucsf', name: 'ucsf', daysSinceActivity: 12 },
  { id: 'ISCBC', name: 'ISCBC', daysSinceActivity: 45 },
  { id: 'star', name: 'star', daysSinceActivity: 120 },
  { id: 'athena-marketing', name: 'athena-marketing', daysSinceActivity: null },
  { id: 'Azta', name: 'Azta', daysSinceActivity: null },
  { id: 'Bellyfit', name: 'Bellyfit', daysSinceActivity: 200 },
  { id: 'bridging-differences-resource-hub', name: 'bridging-differences-resource-hub', daysSinceActivity: null },
  { id: 'cables-and-lace', name: 'cables-and-lace', daysSinceActivity: null },
  { id: 'cal-nerds', name: 'cal-nerds', daysSinceActivity: 95 },
  { id: 'CDA', name: 'CDA', daysSinceActivity: null },
  { id: 'cme-planet', name: 'cme-planet', daysSinceActivity: null },
  { id: 'Cranbrook', name: 'Cranbrook', daysSinceActivity: 180 },
  { id: 'designwerke', name: 'designwerke', daysSinceActivity: null },
  { id: 'exida', name: 'exida', daysSinceActivity: null },
  { id: 'first-foundation', name: 'first-foundation', daysSinceActivity: 60 },
  { id: 'fuel-integrated', name: 'fuel-integrated', daysSinceActivity: null },
  { id: 'haven-oakland', name: 'haven-oakland', daysSinceActivity: 88 },
  { id: 'Hillsdale', name: 'Hillsdale', daysSinceActivity: null },
  { id: 'hop-business', name: 'hop-business', daysSinceActivity: 14 },
  { id: 'hop-marketing-sales', name: 'hop-marketing-sales', daysSinceActivity: null },
]

const INACTIVE_DAY_OPTIONS = [30, 60, 90, 180] as const

function folderVisible(
  folder: MockClientFolder,
  opts: {
    focusBrowse: boolean
    inactiveDays: number
    alwaysShow: Set<string>
    hiddenManual: Set<string>
    activeFolderId: string | null
  }
): 'visible' | 'hidden-auto' | 'hidden-manual' {
  if (!opts.focusBrowse) {
    return opts.hiddenManual.has(folder.id) ? 'hidden-manual' : 'visible'
  }
  if (opts.hiddenManual.has(folder.id)) return 'hidden-manual'
  if (opts.alwaysShow.has(folder.id)) return 'visible'
  if (opts.activeFolderId === folder.id) return 'visible'
  const active =
    folder.daysSinceActivity !== null && folder.daysSinceActivity <= opts.inactiveDays
  if (active) return 'visible'
  return 'hidden-auto'
}

function BrowseSidebarSettingsPanel({
  focusBrowse,
  setFocusBrowse,
  inactiveDays,
  setInactiveDays,
  showActivityInTree,
  setShowActivityInTree,
}: {
  focusBrowse: boolean
  setFocusBrowse: (v: boolean) => void
  inactiveDays: number
  setInactiveDays: (v: number) => void
  showActivityInTree: boolean
  setShowActivityInTree: (v: boolean) => void
}) {
  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h3 className="text-lg font-semibold mb-2">Appearance</h3>
        <p className="text-sm text-muted-foreground mb-4">Theme and layout (existing).</p>
        <div className="rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          Theme, default view mode ...
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-2">Browse sidebar</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Defaults for Documents tree. Day-to-day toggles live on Documents right-click menu.
        </p>
        <div className="space-y-4 rounded-lg border border-border p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="proto-focus-default">Focus browse by default</Label>
              <p className="text-xs text-muted-foreground">
                Hide inactive folders under large parents (e.g. clients/).
              </p>
            </div>
            <Switch
              id="proto-focus-default"
              checked={focusBrowse}
              onCheckedChange={setFocusBrowse}
            />
          </div>
          <div className="space-y-2">
            <Label>Hide folders inactive for</Label>
            <Select
              value={String(inactiveDays)}
              onValueChange={(v) => setInactiveDays(Number(v))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INACTIVE_DAY_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {d} days
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="proto-activity-default">Show activity in tree</Label>
              <p className="text-xs text-muted-foreground">
                Days since your last view/edit next to folder names (e.g. 12d).
              </p>
            </div>
            <Switch
              id="proto-activity-default"
              checked={showActivityInTree}
              onCheckedChange={setShowActivityInTree}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

/** Matches `context-menu.tsx` folder row styling (icon + label). */
function ProdMenuItem({
  icon,
  label,
  onSelect,
  muted,
}: {
  icon: React.ReactNode
  label: string
  onSelect?: () => void
  muted?: boolean
}) {
  return (
    <DropdownMenuItem onSelect={onSelect} className={cn(muted && 'text-muted-foreground')}>
      <span className="mr-2 h-4 w-4 shrink-0 [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
      {label}
    </DropdownMenuItem>
  )
}

/** default = activity rules; always = force visible; hidden = force hidden */
export type FolderBrowseMode = 'default' | 'always' | 'hidden'

const BROWSE_VISIBILITY_SEGMENTS: { id: FolderBrowseMode; label: string }[] = [
  { id: 'default', label: 'Auto' },
  { id: 'always', label: 'Show' },
  { id: 'hidden', label: 'Hide' },
]

/** Single-line 3-way control; pointerDown preventDefault keeps Radix menu open. */
function FolderBrowseVisibilityPill({
  mode,
  onModeChange,
}: {
  mode: FolderBrowseMode
  onModeChange: (mode: FolderBrowseMode) => void
}) {
  return (
    <div
      className="px-2 py-1.5 space-y-1"
      onPointerDown={(e) => e.preventDefault()}
    >
      <Label
        id="folder-browse-visibility-label"
        className="text-[11px] font-medium text-muted-foreground leading-none"
      >
        Browse visibility
      </Label>
      <div
        role="radiogroup"
        aria-labelledby="folder-browse-visibility-label"
        className="flex w-full rounded-md border border-border bg-muted/50 p-0.5"
      >
        {BROWSE_VISIBILITY_SEGMENTS.map((seg) => (
          <button
            key={seg.id}
            type="button"
            role="radio"
            aria-checked={mode === seg.id}
            className={cn(
              'flex-1 rounded-[3px] px-0.5 py-1 text-[11px] font-medium leading-none transition-colors',
              mode === seg.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => {
              if (seg.id !== mode) onModeChange(seg.id)
            }}
          >
            {seg.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/** Toggle row (dropdown-menu has no CheckboxItem export). */
function MenuToggleItem({
  checked,
  onCheckedChange,
  children,
  disabled,
}: {
  checked: boolean
  onCheckedChange: (v: boolean) => void
  children: React.ReactNode
  disabled?: boolean
}) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      onSelect={(e) => {
        e.preventDefault()
        onCheckedChange(!checked)
      }}
      className="gap-2"
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
        {checked ? <Check className="h-3.5 w-3.5" /> : null}
      </span>
      <span className="flex-1">{children}</span>
    </DropdownMenuItem>
  )
}

type BrowseMenuProps = {
  focusBrowse: boolean
  onFocusBrowseChange: (v: boolean) => void
  inactiveDays: number
  onInactiveDaysChange: (d: number) => void
  showActivityInTree: boolean
  onShowActivityInTreeChange: (v: boolean) => void
  hiddenCount: number
  onOpenBrowsePreferences: () => void
  onLog?: (msg: string) => void
}

/**
 * Production folder menu from `context-menu.tsx` (folder case) + browse extensions.
 * - `documents-root`: Documents row right-click (workspace tree parent)
 * - `folder`: any tree folder (e.g. clients) - your screenshot
 */
type FolderBrowsePrefs = {
  mode: FolderBrowseMode
  onModeChange: (mode: FolderBrowseMode) => void
}

function ProductionFolderContextMenu({
  variant,
  folderLabel,
  children,
  browse,
  folderPrefs,
  onFolderPrefAction,
}: {
  variant: 'documents-root' | 'folder'
  folderLabel: string
  children: React.ReactNode
  browse: BrowseMenuProps
  /** Per-folder row only: Auto | Show | Hide pill. */
  folderPrefs?: FolderBrowsePrefs
  onFolderPrefAction?: (action: 'show-all-hidden') => void
}) {
  const showAllHidden =
    variant === 'folder' &&
    folderLabel === 'clients' &&
    browse.focusBrowse &&
    browse.hiddenCount > 0

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {/* --- Existing production (context-menu.tsx folder case) --- */}
        <ProdMenuItem icon={<Search />} label={`Search in ${folderLabel}`} />
        <ProdMenuItem icon={<Eye />} label="Preview" />
        <DropdownMenuSeparator />
        <ProdMenuItem icon={<FilePlus />} label="New File" />
        <ProdMenuItem icon={<FolderPlus />} label="New Folder" />
        <DropdownMenuSeparator />
        <ProdMenuItem icon={<Pencil />} label="Rename" />
        <ProdMenuItem icon={<Move />} label="Move to..." />

        {/* --- NEW: browse (Documents root only) --- */}
        {variant === 'documents-root' && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              Browse
            </div>
            <MenuToggleItem
              checked={browse.focusBrowse}
              onCheckedChange={(v) => {
                browse.onFocusBrowseChange(v)
                browse.onLog?.(v ? 'Focus browse on' : 'Focus browse off (show all)')
              }}
            >
              Focus browse
            </MenuToggleItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={!browse.focusBrowse} className="text-sm">
                Hide inactive for...
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {INACTIVE_DAY_OPTIONS.map((d) => (
                  <DropdownMenuItem
                    key={d}
                    onSelect={() => browse.onInactiveDaysChange(d)}
                  >
                    {d} days
                    {browse.inactiveDays === d && (
                      <Check className="h-3.5 w-3.5 ml-auto" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <MenuToggleItem
              checked={browse.showActivityInTree}
              onCheckedChange={browse.onShowActivityInTreeChange}
            >
              Show activity in tree
            </MenuToggleItem>
          </>
        )}

        {/* --- NEW: parent folder (e.g. clients) when focus on --- */}
        {showAllHidden && (
          <>
            <DropdownMenuSeparator />
            <ProdMenuItem
              icon={<Eye />}
              label={`Show all folders (${browse.hiddenCount} hidden)`}
              onSelect={() => onFolderPrefAction?.('show-all-hidden')}
            />
          </>
        )}

        {/* --- NEW: per-folder visibility (one row: Auto | Show | Hide) --- */}
        {variant === 'folder' && folderPrefs && (
          <>
            <DropdownMenuSeparator />
            <FolderBrowseVisibilityPill
              mode={folderPrefs.mode}
              onModeChange={folderPrefs.onModeChange}
            />
          </>
        )}

        {/* --- Existing: workspace Settings (unchanged) --- */}
        <DropdownMenuSeparator />
        <ProdMenuItem icon={<Settings />} label="Settings" />
        {variant === 'documents-root' && (
          <ProdMenuItem
            icon={<Filter />}
            label="Browse preferences..."
            onSelect={browse.onOpenBrowsePreferences}
          />
        )}
        <DropdownMenuSeparator />
        <ProdMenuItem icon={<MessageSquare />} label="Add to current chat" />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ClientFolderRowContent({
  folder,
  muted,
  showActivityInTree,
  alwaysShow,
  hiddenManual,
}: {
  folder: MockClientFolder
  muted: boolean
  showActivityInTree: boolean
  alwaysShow: boolean
  hiddenManual: boolean
}) {
  return (
    <>
      <span className="w-3.5 shrink-0" />
      <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate flex-1 font-mono text-xs">{folder.name}</span>
      {alwaysShow && (
        <Badge variant="outline" className="text-[9px] h-3.5 px-1 border-primary/40 text-primary">
          always
        </Badge>
      )}
      {hiddenManual && (
        <Badge variant="outline" className="text-[9px] h-3.5 px-1 text-muted-foreground">
          hidden
        </Badge>
      )}
      {showActivityInTree && folder.daysSinceActivity !== null && (
        <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
          {folder.daysSinceActivity}d
        </span>
      )}
      {showActivityInTree && folder.daysSinceActivity === null && (
        <span className="text-[10px] text-muted-foreground/50 shrink-0">-</span>
      )}
    </>
  )
}

export function BrowseFolderFocusTest({
  initialTreeTab = 'clients',
}: {
  initialTreeTab?: 'clients' | 'specs' | 'mix'
} = {}) {
  const activitySeed = useMemo(() => buildPrototypeActivitySeed(), [])
  return (
    <BrowseFolderFocusProvider
      workspaceIds={PROTOTYPE_WORKSPACE_IDS}
      activitySeedEntries={activitySeed}
    >
      <TooltipProvider>
        <BrowseFolderFocusTestInner initialTreeTab={initialTreeTab} />
        <Toaster />
      </TooltipProvider>
    </BrowseFolderFocusProvider>
  )
}

function BrowseFolderFocusTestInner({
  initialTreeTab,
}: {
  initialTreeTab: 'clients' | 'specs' | 'mix'
}) {
  const browseFocus = useBrowseFolderFocus()
  const [mainTab, setMainTab] = useState<'browse' | 'settings'>('browse')
  const [treeTab, setTreeTab] = useState<'clients' | 'specs' | 'mix'>(initialTreeTab)
  const focusBrowse = browseFocus?.focusBrowse ?? true
  const setFocusBrowse = browseFocus?.setFocusBrowse ?? (() => {})
  const inactiveDays = browseFocus?.inactiveDays ?? 90
  const setInactiveDays = (d: number) =>
    browseFocus?.setInactiveDays(d as BrowseInactiveDays)
  const showActivityInTree = browseFocus?.showActivityInTree ?? false
  const setShowActivityInTree = browseFocus?.setShowActivityInTree ?? (() => {})
  const clientsRevealed =
    browseFocus?.isBrowseParentRevealed(MOCK_CLIENTS_PARENT) ?? false
  const [alwaysShow, setAlwaysShow] = useState<Set<string>>(() => new Set(['ggia']))
  const [hiddenManual, setHiddenManual] = useState<Set<string>>(() => new Set())
  const [clientsExpanded, setClientsExpanded] = useState(true)
  const [documentsExpanded, setDocumentsExpanded] = useState(true)
  const [activeFolderId, setActiveFolderId] = useState<string | null>('greater-good')
  const [contextLog, setContextLog] = useState<string | null>(null)

  const visibility = useMemo(() => {
    const opts = { focusBrowse, inactiveDays, alwaysShow, hiddenManual, activeFolderId }
    return MOCK_CLIENTS.map((f) => ({
      folder: f,
      state: folderVisible(f, opts),
    }))
  }, [focusBrowse, inactiveDays, alwaysShow, hiddenManual, activeFolderId])

  const visibleRows = visibility.filter((v) => v.state === 'visible')
  const hiddenAutoRows = visibility.filter((v) => v.state === 'hidden-auto')
  const hiddenManualRows = visibility.filter((v) => v.state === 'hidden-manual')
  const hiddenCount = hiddenAutoRows.length + hiddenManualRows.length

  // Focus on: hide auto-inactive unless this parent was revealed (per-parent show all)
  const rowsToRender =
    focusBrowse && !clientsRevealed
      ? visibility.filter((v) => v.state !== 'hidden-auto')
      : visibility

  const browseMenu: BrowseMenuProps = useMemo(
    () => ({
      focusBrowse,
      onFocusBrowseChange: setFocusBrowse,
      inactiveDays,
      onInactiveDaysChange: setInactiveDays,
      showActivityInTree,
      onShowActivityInTreeChange: setShowActivityInTree,
      hiddenCount,
      onOpenBrowsePreferences: () => setMainTab('settings'),
      onLog: setContextLog,
    }),
    [focusBrowse, inactiveDays, showActivityInTree, hiddenCount]
  )

  const folderBrowseMode = useCallback(
    (folderId: string): FolderBrowseMode => {
      if (hiddenManual.has(folderId)) return 'hidden'
      if (alwaysShow.has(folderId)) return 'always'
      return 'default'
    },
    [alwaysShow, hiddenManual]
  )

  const setFolderBrowseMode = useCallback((folderId: string, mode: FolderBrowseMode) => {
    setAlwaysShow((prev) => {
      const next = new Set(prev)
      if (mode === 'always') next.add(folderId)
      else next.delete(folderId)
      return next
    })
    setHiddenManual((prev) => {
      const next = new Set(prev)
      if (mode === 'hidden') next.add(folderId)
      else next.delete(folderId)
      return next
    })
    const labels: Record<FolderBrowseMode, string> = {
      default: 'Auto (activity rules)',
      always: 'Show (always in tree)',
      hidden: 'Hide (excluded while focus on)',
    }
    setContextLog(`${folderId}: ${labels[mode]}`)
  }, [])

  const folderPrefsFor = useCallback(
    (folderId: string): FolderBrowsePrefs => ({
      mode: folderBrowseMode(folderId),
      onModeChange: (m) => setFolderBrowseMode(folderId, m),
    }),
    [folderBrowseMode, setFolderBrowseMode]
  )

  return (
    <div className="h-screen flex flex-col bg-background text-foreground">
      <header className="border-b px-4 py-3 flex items-center gap-3 shrink-0">
        <Filter className="h-5 w-5 text-primary" />
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold truncate">Browse Focus (hybrid)</h1>
          <p className="text-xs text-muted-foreground truncate">
            Clients mock + production specs tree (gutter, files, show hidden)
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <a href="/?test=index">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            Index
          </a>
        </Button>
      </header>

      <Tabs
        value={mainTab}
        onValueChange={(v) => setMainTab(v as 'browse' | 'settings')}
        className="flex-1 flex flex-col min-h-0"
      >
        <TabsList className="mx-4 mt-3 w-auto justify-start">
          <TabsTrigger value="browse">Browse + tree</TabsTrigger>
          <TabsTrigger value="settings">UI Preferences (settings)</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="flex-1 flex min-h-0 mt-0 data-[state=inactive]:hidden">
          <div className="flex flex-1 min-h-0 border-t">
            <aside
              className="w-[272px] shrink-0 border-r bg-sidebar flex flex-col text-sm"
              style={{ '--ws-color': '#6366f1' } as React.CSSProperties}
            >
              <div className="px-3 py-2 border-b border-sidebar-border font-medium text-sidebar-foreground">
                Browse
              </div>
              <div className="flex-1 overflow-y-auto px-1 py-2 space-y-1">
                <Tabs
                  value={treeTab}
                  onValueChange={(v) => setTreeTab(v as 'clients' | 'specs' | 'mix')}
                  className="px-1 mb-2"
                >
                  <TabsList className="w-full h-7 grid grid-cols-3">
                    <TabsTrigger value="clients" className="text-[10px] px-1">
                      Menu mock
                    </TabsTrigger>
                    <TabsTrigger value="mix" className="text-[10px] px-1">
                      Clients mix
                    </TabsTrigger>
                    <TabsTrigger value="specs" className="text-[10px] px-1">
                      Specs
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {treeTab === 'specs' ? (
                  <div
                    className="ws-nav-section min-h-[200px]"
                    style={{ '--ws-color': '#14b8a6' } as React.CSSProperties}
                  >
                    <BrowseSpecsProductionTree />
                  </div>
                ) : treeTab === 'mix' ? (
                  <div
                    className="ws-nav-section min-h-[200px]"
                    style={{ '--ws-color': '#6366f1' } as React.CSSProperties}
                  >
                    <BrowseClientsMixProductionTree />
                  </div>
                ) : (
                <>
                <div className="flex items-center gap-2 px-2 py-1">
                  <Building2 className="h-4 w-4" style={{ color: '#6366f1' }} />
                  <span className="font-medium">Hop Studios</span>
                </div>

                <div className="pl-2 space-y-0.5">
                  <NavRowMock label="Basecamp" icon={<Boxes className="h-3.5 w-3.5" />} />
                  <NavRowMock label="Pinned" icon={<Pin className="h-3.5 w-3.5" />} expanded />
                  <div className="pl-6 pr-1 text-xs text-muted-foreground truncate">
                    Product Board.md
                  </div>

                  <ProductionFolderContextMenu
                    variant="documents-root"
                    folderLabel="Documents"
                    browse={browseMenu}
                  >
                    <button
                      type="button"
                      className={cn(
                        'flex items-center gap-1.5 w-full px-2 py-1 rounded hover:bg-sidebar-accent text-left',
                        'ring-1 ring-transparent focus-visible:ring-primary/40'
                      )}
                      onClick={() => setDocumentsExpanded(!documentsExpanded)}
                      title="Right-click for menu (production + Browse section)"
                    >
                      {documentsExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                      )}
                      <FolderOpen className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate flex-1">Documents</span>
                      {focusBrowse && hiddenCount > 0 && (
                        <Badge variant="outline" className="text-[10px] h-4 px-1 font-normal shrink-0">
                          focus
                        </Badge>
                      )}
                    </button>
                  </ProductionFolderContextMenu>

                  {documentsExpanded && (
                    <div className="pl-4 pr-1 space-y-0.5">
                      <div className="py-0.5">
                        <button
                          type="button"
                          className="flex items-center gap-1.5 w-full font-mono text-xs rounded-sm hover:bg-sidebar-accent/50"
                          onClick={() => {}}
                        >
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          <Folder className="h-3.5 w-3.5" />
                          <span>.soul</span>
                        </button>
                      </div>

                        <ProductionFolderContextMenu
                          variant="folder"
                          folderLabel="clients"
                          browse={browseMenu}
                          onFolderPrefAction={(a) => {
                            if (a === 'show-all-hidden') {
                              browseFocus?.revealBrowseParent(MOCK_CLIENTS_PARENT)
                              setContextLog('Show all hidden (clients) -> per-parent reveal')
                            }
                          }}
                        >
                          <button
                            type="button"
                            className={cn(
                              'flex items-center gap-1.5 w-full py-0.5 font-mono text-xs rounded-sm',
                              'hover:bg-sidebar-accent ring-1 ring-transparent focus-visible:ring-primary/40'
                            )}
                            onClick={() => setClientsExpanded(!clientsExpanded)}
                            title="Right-click - same menu as your screenshot"
                          >
                            {clientsExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                            <Folder className="h-3.5 w-3.5" />
                            <span>clients</span>
                            {focusBrowse && (
                              <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
                                {visibleRows.length}/{MOCK_CLIENTS.length}
                              </span>
                            )}
                          </button>
                        </ProductionFolderContextMenu>

                        {clientsExpanded && (
                          <div className="pl-4 mt-0.5 space-y-0.5">
                            {rowsToRender.map(({ folder, state }) => (
                                <ProductionFolderContextMenu
                                  key={folder.id}
                                  variant="folder"
                                  folderLabel={folder.name}
                                  browse={browseMenu}
                                  folderPrefs={folderPrefsFor(folder.id)}
                                >
                                  <button
                                    type="button"
                                    className={cn(
                                      'group flex items-center gap-1 min-w-0 rounded-sm py-0.5 pr-1 text-sm w-full text-left',
                                      state === 'hidden-auto' && 'opacity-40',
                                      state === 'hidden-manual' && 'opacity-50',
                                      state !== 'visible' && state !== 'hidden-manual' && 'opacity-55',
                                      activeFolderId === folder.id &&
                                        'bg-primary/15 ring-1 ring-primary/30'
                                    )}
                                    onClick={() => setActiveFolderId(folder.id)}
                                  >
                                    <ClientFolderRowContent
                                      folder={folder}
                                      muted={state !== 'visible'}
                                      showActivityInTree={showActivityInTree}
                                      alwaysShow={alwaysShow.has(folder.id)}
                                      hiddenManual={hiddenManual.has(folder.id)}
                                    />
                                  </button>
                                </ProductionFolderContextMenu>
                            ))}

                            {focusBrowse && hiddenCount > 0 && !clientsRevealed && (
                              <button
                                type="button"
                                className="browse-show-hidden-link"
                                style={
                                  {
                                    '--browse-show-hidden-pad-left': '2.5rem',
                                  } as React.CSSProperties
                                }
                                onClick={() => {
                                  browseFocus?.revealBrowseParent(MOCK_CLIENTS_PARENT)
                                  setContextLog('Show all hidden -> reveal clients parent only')
                                }}
                              >
                                <Eye className="h-3 w-3 shrink-0" />
                                Show {hiddenCount} hidden folders
                              </button>
                            )}
                          </div>
                        )}
                    </div>
                  )}

                  <NavRowMock label="Product Board" icon={<LayoutGrid className="h-3.5 w-3.5" />} />
                </div>
                </>
                )}
              </div>
            </aside>

            <main className="flex-1 overflow-auto p-6 space-y-4">
              <div className="max-w-xl space-y-3">
                <h2 className="text-base font-semibold">What to try</h2>
                <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
                  <li>
                    <strong>Clients mix</strong> tab: 22 client folders; expand <strong>greater-good</strong> for
                    subfolders + files in one list (same rules each).
                  </li>
                  <li>
                    <strong>Specs</strong> tab: many files under archive (file-heavy parent).
                  </li>
                  <li>
                    <strong>Menu mock</strong>: context menus + simplified clients list (no production node).
                  </li>
                  <li>
                    <strong>Live app - turn focus on:</strong> right-click the{' '}
                    <strong>Documents</strong> row (workspace tree, not a folder file) and check{' '}
                    <strong>Focus browse</strong>. Or Settings - UI Preferences - Browse sidebar.
                  </li>
                  <li>
                    Needs a large parent (20+ children by default) and recent activity data; expand a
                    folder like <strong>clients/</strong> or <strong>implementations/archive</strong>.
                  </li>
                  <li>
                    <strong>Show hidden</strong>: italic link under folder, gutter icon, or folder context menu.
                  </li>
                </ul>
                <div className="text-xs font-mono text-muted-foreground border border-border rounded p-3 space-y-1 max-w-lg">
                  <div className="text-foreground font-sans font-medium mb-1">Menu placement (impl)</div>
                  <div>Documents: ... Move to | Browse* | Settings | Browse prefs | Add to chat</div>
                  <div>clients: ... Move to | Show all* | Settings | Add to chat</div>
                  <div>client folder: ... Move to | [Auto|Show|Hide]* | Settings | Add to chat</div>
                  <div className="text-muted-foreground/80">* = new</div>
                </div>
                {contextLog && (
                  <p className="text-xs font-mono text-primary border border-primary/20 rounded px-2 py-1">
                    {contextLog}
                  </p>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-3 max-w-2xl text-sm">
                <StatCard label="Visible (focus on)" value={visibleRows.length} />
                <StatCard label="Hidden" value={hiddenCount} />
                <StatCard
                  label="Focus browse"
                  value={focusBrowse ? 1 : 0}
                  labelSuffix={focusBrowse ? 'on' : 'off'}
                />
              </div>
            </main>
          </div>
        </TabsContent>

        <TabsContent
          value="settings"
          className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden overflow-auto"
        >
          <div className="flex h-full min-h-[480px]">
            <nav className="w-52 shrink-0 border-r bg-muted/20 p-3 space-y-4 text-sm">
              <div className="font-semibold px-2">Settings</div>
              <div className="text-xs text-muted-foreground px-2">
                <div className="pl-2 rounded bg-primary/15 text-foreground font-medium">
                  UI Preferences
                </div>
              </div>
            </nav>
            <div className="flex-1 p-8 overflow-auto">
              <BrowseSidebarSettingsPanel
                focusBrowse={focusBrowse}
                setFocusBrowse={setFocusBrowse}
                inactiveDays={inactiveDays}
                setInactiveDays={setInactiveDays}
                showActivityInTree={showActivityInTree}
                setShowActivityInTree={setShowActivityInTree}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function NavRowMock({
  label,
  icon,
  indent,
  expanded,
}: {
  label: string
  icon: React.ReactNode
  indent?: boolean
  expanded?: boolean
}) {
  return (
    <div className={cn('flex items-center gap-1.5 px-2 py-0.5', indent && 'pl-5')}>
      {expanded !== undefined ? (
        expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )
      ) : (
        <span className="w-3.5" />
      )}
      {icon}
      <span className="text-xs">{label}</span>
    </div>
  )
}

function StatCard({
  label,
  value,
  labelSuffix,
}: {
  label: string
  value: number
  labelSuffix?: string
}) {
  return (
    <div className="rounded-lg border border-border p-3 bg-card">
      <div className="text-2xl font-semibold tabular-nums">
        {labelSuffix ?? value}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}
